import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getInvestorOverview } from '../../api/investorApi';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { KpiCard } from '../../components/ui/KpiCard';
import { StatusPill } from '../../components/ui/StatusPill';
import {
  IconAlert,
  IconBuilding,
  IconClipboard,
  IconInbox,
  IconLayers,
  IconPackage,
  IconShield,
  IconVault,
} from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import { humanize } from './investorLabels';

export function InvestorOverviewPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getInvestorOverview();
        if (!cancelled) setOverview(response);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load the overview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const kpis = overview?.kpis;
  const orgs = overview?.organizations;

  const cards = useMemo(
    () => [
      { label: 'Packages', value: kpis?.received ?? 0, subline: 'Across every originator', icon: IconPackage, accent: true },
      { label: 'Blocked', value: kpis?.blocked ?? 0, subline: 'Open exceptions', icon: IconAlert },
      { label: 'Awaiting Provider review', value: kpis?.awaiting_provider_review ?? 0, subline: 'Certified, authority pending', icon: IconInbox },
      { label: 'Awaiting Custodian', value: kpis?.awaiting_custodian ?? 0, subline: 'Routed, pending authority', icon: IconShield },
      { label: 'Admitted', value: kpis?.admitted ?? 0, subline: 'Custody accepted', icon: IconVault },
    ],
    [kpis]
  );

  const platformCards = useMemo(
    () => [
      {
        label: 'Organizations',
        value:
          (orgs?.originator ?? 0) + (orgs?.provider ?? 0) + (orgs?.custodian ?? 0) + (orgs?.investor ?? 0),
        subline: orgs
          ? `${orgs.originator} originators · ${orgs.custodian} custodians · ${orgs.provider} providers`
          : undefined,
        icon: IconBuilding,
      },
      { label: 'Programs', value: overview?.programs ?? 0, subline: 'Originator programs', icon: IconLayers },
      { label: 'Assets', value: overview?.assets ?? 0, subline: 'Loans under packages', icon: IconVault },
      { label: 'Events (24h)', value: overview?.activity_last_24h ?? 0, subline: 'Transactions recorded', icon: IconClipboard },
    ],
    [orgs, overview]
  );

  const pipeline = overview?.pipeline || [];
  const maxPipeline = Math.max(1, ...pipeline.map((stage) => stage.count || 0));

  const columns = useMemo(
    () => [
      {
        key: 'package',
        header: 'Package',
        render: (row) => (
          <div className="min-w-0">
            <Link
              to={`/investor/packages/${row.package_id}`}
              onClick={(e) => e.stopPropagation()}
              className="block font-semibold text-eorig-ink hover:underline"
            >
              {row.reference_number || row.package_id}
            </Link>
            <span className="block text-[11px] text-eorig-muted">{formatDateTime(row.created_at)}</span>
          </div>
        ),
      },
      { key: 'originator', header: 'Originator' },
      { key: 'program', header: 'Program' },
      {
        key: 'package_status',
        header: 'Status',
        render: (row) => <StatusPill value={row.package_status} label={humanize(row.package_status)} />,
      },
      {
        key: 'authority_status',
        header: 'Authority',
        render: (row) => <StatusPill value={row.authority_status} label={humanize(row.authority_status)} />,
      },
      {
        key: 'custody_status',
        header: 'Custody',
        render: (row) => <StatusPill value={row.custody_status} label={humanize(row.custody_status)} />,
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-eorig-muted">
        Everything on the platform, read-only: who did what, and when.
      </p>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {cards.map((card) => (
          <KpiCard key={card.label} {...card} loading={loading} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {platformCards.map((card) => (
          <KpiCard key={card.label} {...card} loading={loading} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <div className="lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-eorig-ink">Recent packages</h2>
            <Link
              to="/investor/packages"
              className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              View all
            </Link>
          </div>
          <DataTable
            columns={columns}
            rows={overview?.recent_packages || []}
            rowKey={(row) => row.package_id}
            rowHref={(row) => `/investor/packages/${row.package_id}`}
            loading={loading}
            emptyMessage="No packages yet."
            minWidth={720}
          />
        </div>

        <section className="flex flex-col rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
          <h2 className="text-sm font-semibold text-eorig-ink">Pipeline</h2>
          <ul className="mt-3 space-y-3">
            {pipeline.length === 0 && !loading ? (
              <li className="text-xs text-eorig-muted">No pipeline data.</li>
            ) : null}
            {pipeline.map((stage) => (
              <li key={stage.stage}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-eorig-ink">{stage.label}</span>
                  <span className="font-semibold text-eorig-ink">{stage.count}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-eorig-surface">
                  <div
                    className="h-1.5 rounded-full bg-eorig-gradient"
                    style={{ width: `${Math.round(((stage.count || 0) / maxPipeline) * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
          <Link
            to="/investor/activity"
            className="mt-4 text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            Open the activity timeline
          </Link>
        </section>
      </div>
    </div>
  );
}
